import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { useRevokeApiKeyMutation } from '@hooks/requests/queries/use_revoke_api_key_mutation';
import { useToast } from '@hooks/useToast';
import { describeMutationError } from '@hooks/requests/utils/_partner_api_client';
import type { PartnerApiKey } from '@hooks/requests/queries/use_partner_api_keys_query';
import { Modal, Button, Alert } from '../ui';

interface RevokeApiKeyConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiKey: PartnerApiKey | null;
}

const keyLabel = (apiKey: PartnerApiKey): string => apiKey.name || 'This key';

/**
 * Delete API key confirmation modal (sc-45103, AC4-AC7; Figma 3087:15048
 * "Modal - Invite member", the "Delete API Key" copy the frame actually
 * renders). The user-facing verb here is "Delete", matching this frame
 * exactly — title, body copy, and the red "Delete this key" button — even
 * though the surrounding code (this file's name, the mutation hook, prop
 * names) still says "revoke", since renaming those is a separate, much
 * wider refactor nobody asked for. `ApiKeysTable` only opens this once it
 * already knows the account has 2 keys (AC3's 1-key-floor toast happens
 * before the modal, at the row menu); the 409 "last_key" path below only
 * fires on the rare race where a second admin deleted the other key first.
 */
const RevokeApiKeyConfirmationModal: React.FC<RevokeApiKeyConfirmationModalProps> = ({
  isOpen,
  onClose,
  apiKey,
}) => {
  const [error, setError] = useState('');
  const revokeMutation = useRevokeApiKeyMutation();
  const { showToast } = useToast();

  const handleClose = () => {
    setError('');
    onClose();
  };

  const handleRevoke = async () => {
    if (!apiKey) return;
    setError('');

    try {
      await revokeMutation.mutateAsync({ maskedKey: apiKey.maskedKey });
      showToast(`"${keyLabel(apiKey)}" was successfully deleted.`);
      handleClose();
    } catch (err) {
      setError(
        describeMutationError(err, {
          notFound: `${keyLabel(apiKey)} no longer exists in this organization.`,
          codeMessages: {
            last_key: 'You need a second key before you can delete this one.',
            protected: 'This key is protected. You can not remove it.',
          },
          fallback: 'Failed to delete the API key',
        })
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Delete API Key"
      headerVariant="tinted"
      className="api-keys-modal"
      bodyClassName="api-keys-modal__body"
      footerClassName="api-keys-modal__foot"
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" className="au-modal-pill-btn--cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            className="au-modal-pill-btn--danger"
            leftIcon={<FontAwesomeIcon icon={faTrashAlt} />}
            onClick={handleRevoke}
            isLoading={revokeMutation.isPending}
          >
            Delete this key
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {apiKey && (
          <p className="au-modal-context-line">
            &ldquo;{keyLabel(apiKey)}&rdquo; may still be in use
            {apiKey.isActive && " — it's the key that most recently authenticated a request"}.
            Anything using it will stop working immediately.
            <br />
            <br />
            <span className="font-bold">This action can&apos;t be undone.</span>
          </p>
        )}

        {error && <Alert variant="danger">{error}</Alert>}
      </div>
    </Modal>
  );
};

export default RevokeApiKeyConfirmationModal;
